import { css } from 'aphrodite';
import React, { useEffect, useState, useCallback, useMemo } from 'react';

import { orgsApi, rolesApi, InviteSummary, Member, RoleSummary } from '@/features/org/api/orgs.api';
import InviteModal from '@/features/org/components/members/invite-modal/InviteModal';
import { orgStyles as s } from '@/features/org/components/org.styles';
import SettingsLayout from '@/features/org/components/settings/settings-layout/SettingsLayout';
import { useOrgs } from '@/features/org/hooks/use-orgs/useOrgs';
import { useParams, useHistory } from '@/shared/hooks/use-router/react-router-hooks';

/**
 * /o/:slug/members — full-page member list. Owners + admins can change
 * roles, remove members, create invites, and see + revoke pending ones.
 *
 * The role dropdown is populated from the org's ACTUAL roles
 * (rolesApi.list) rather than a hardcoded list — both the built-in roles
 * (Admin / Developer / PM / Viewer) AND any custom roles the org defined on
 * the Roles tab. `owner` is never offered as an assignable option — ownership
 * is transferred, not granted (the backend 409s on a second owner).
 */

type RoleOption = { id: string; label: string; isBuiltin: boolean };

// Priority order for the built-in slugs; custom roles sort after them by name.
const BUILTIN_ORDER: Record<string, number> = { admin: 4, developer: 3, pm: 2, viewer: 1 };

// Fallback options used only until rolesApi resolves (or if it fails). The
// assignable built-ins, owner excluded.
const FALLBACK_ROLE_OPTIONS: RoleOption[] = [
  { id: 'admin', label: 'Admin', isBuiltin: true },
  { id: 'developer', label: 'Developer', isBuiltin: true },
  { id: 'pm', label: 'PM', isBuiltin: true },
  { id: 'viewer', label: 'Viewer', isBuiltin: true },
];

// An invite that can still be redeemed: not revoked, not used up, not expired.
function isPending(i: InviteSummary): boolean {
  if (i.revoked || (!i.isMultiUse && i.used)) return false;
  if (i.isMultiUse && i.maxUses != null && i.useCount >= i.maxUses) return false;
  return !i.expiresAt || new Date(i.expiresAt).getTime() > Date.now();
}

// Colour chip for a member's role. Built-ins get their branded colour; custom
// roles fall back to the neutral viewer chip.
function roleChipClass(m: Member): string {
  if (!m.isBuiltin) return css(s.roleChip, s.roleViewer);
  if (m.roleId === 'owner') return css(s.roleChip, s.roleOwner);
  if (m.roleId === 'admin') return css(s.roleChip, s.roleAdmin);
  if (m.roleId === 'developer') return css(s.roleChip, s.roleDeveloper);
  if (m.roleId === 'pm') return css(s.roleChip, s.rolePm);
  return css(s.roleChip, s.roleViewer);
}

export default function MembersPage() {
  const { slug } = useParams<{ slug: string }>();
  const history = useHistory();
  const { switchOrg, myRole } = useOrgs();

  const [members, setMembers] = useState<Member[] | null>(null);
  const [roles, setRoles] = useState<RoleSummary[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showInvite, setShowInvite] = useState(false);
  const [invites, setInvites] = useState<InviteSummary[] | null>(null);

  // Keep the active-org in sync with the URL slug so X-Active-Org points
  // at this org for the API calls below.
  useEffect(() => {
    switchOrg(slug);
  }, [slug, switchOrg]);

  // Load the org's real roles so the dropdown reflects actual roles instead
  // of a hardcoded list. Needs the org id (roles are keyed by org id).
  useEffect(() => {
    let cancelled = false;
    orgsApi
      .bySlug(slug)
      .then((r) => rolesApi.list(r.org.id))
      .then((r) => {
        if (!cancelled) setRoles(r.roles);
      })
      .catch(() => {
        /* fall back to FALLBACK_ROLE_OPTIONS below */
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Assignable options — the org's real roles: built-ins (owner excluded) plus
  // every custom role. Built-ins first (by priority), then custom roles A→Z.
  const roleOptions = useMemo<RoleOption[]>(() => {
    if (!roles) return FALLBACK_ROLE_OPTIONS;
    const opts: RoleOption[] = roles
      .filter((r) => r.id !== 'owner')
      .map((r) => ({ id: r.id, label: r.name, isBuiltin: r.isBuiltin }));
    opts.sort((a, b) => {
      const ap = a.isBuiltin ? BUILTIN_ORDER[a.id] || 0 : -1;
      const bp = b.isBuiltin ? BUILTIN_ORDER[b.id] || 0 : -1;
      if (ap !== bp) return bp - ap; // built-ins (higher priority) first
      return a.label.localeCompare(b.label); // then custom roles alphabetically
    });
    return opts.length ? opts : FALLBACK_ROLE_OPTIONS;
  }, [roles]);

  const reload = useCallback(async () => {
    try {
      const res = await orgsApi.members(slug);
      setMembers(res.members);
      setErr(null);
    } catch (e: any) {
      setErr(e.message || 'Failed to load members');
    }
  }, [slug]);

  useEffect(() => {
    reload();
  }, [reload]);

  const canManage = myRole === 'owner' || myRole === 'admin';

  // Pending invites — only owners/admins can list them (users:invite).
  const reloadInvites = useCallback(async () => {
    try {
      const res = await orgsApi.invites.list(slug);
      setInvites(res.invites.filter(isPending));
    } catch (e: any) {
      setErr(e.message || 'Failed to load invites');
    }
  }, [slug]);

  useEffect(() => {
    if (canManage) reloadInvites();
  }, [canManage, reloadInvites]);

  const handleRevoke = async (i: InviteSummary) => {
    if (
      !confirm(`Revoke the invite ${i.email ? `for ${i.email}` : 'link'}? Its link stops working.`)
    )
      return;
    try {
      await orgsApi.invites.revoke(slug, i.id);
      reloadInvites();
    } catch (e: any) {
      setErr(e.message || 'Failed to revoke invite');
    }
  };

  const handleRoleChange = async (m: Member, nextRoleId: string) => {
    if (nextRoleId === m.roleId) return;
    try {
      await orgsApi.setMemberRole(slug, m.userId, nextRoleId);
      reload();
    } catch (e: any) {
      setErr(e.message || 'Failed to change role');
    }
  };

  const handleRemove = async (m: Member) => {
    if (!confirm(`Remove ${m.email} from this org? Their app-level grants stay intact.`)) return;
    try {
      await orgsApi.removeMember(slug, m.userId);
      reload();
    } catch (e: any) {
      setErr(e.message || 'Failed to remove member');
    }
  };

  return (
    <SettingsLayout>
      <div className={css(s.page)}>
        <div className={css(s.pageHeader)}>
          <div>
            <div className={css(s.pageTitle)}>Members</div>
            <div className={css(s.pageSubtitle)}>
              People with access to this org. The role each member holds determines what they can
              do.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className={css(s.btnGhost)}
              onClick={() => history.push(`/o/${slug}/dashboard`)}
            >
              ← Back
            </button>
            {canManage && (
              <button className={css(s.btn)} onClick={() => setShowInvite(true)}>
                + Invite member
              </button>
            )}
          </div>
        </div>

        {err && <div className={css(s.errorBox)}>{err}</div>}

        {!members ? (
          <div className={css(s.empty)}>Loading…</div>
        ) : members.length === 0 ? (
          <div className={css(s.empty)}>No members yet.</div>
        ) : (
          <table className={css(s.table)}>
            <thead>
              <tr>
                <th className={css(s.th)}>Email</th>
                <th className={css(s.th)}>Name</th>
                <th className={css(s.th)}>Role</th>
                <th className={css(s.th)}>Joined</th>
                <th className={css(s.th)} />
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.userId}>
                  <td className={css(s.td)}>{m.email}</td>
                  <td className={css(s.td)}>{m.name || '—'}</td>
                  <td className={css(s.td)}>
                    {canManage && m.roleId !== 'owner' ? (
                      <select
                        className={css(s.select)}
                        value={m.roleId}
                        style={{ width: 'auto', padding: '4px 8px', fontSize: 11 }}
                        onChange={(e) => handleRoleChange(m, e.target.value)}
                      >
                        {/* If the member's current role somehow isn't in the
                          assignable set, still show it so the select reflects
                          reality (and isn't silently coerced to the first item). */}
                        {!roleOptions.some((o) => o.id === m.roleId) && (
                          <option value={m.roleId}>{m.roleName}</option>
                        )}
                        {roleOptions.map((o) => (
                          <option key={o.id} value={o.id}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className={roleChipClass(m)}>{m.roleName}</span>
                    )}
                  </td>
                  <td className={css(s.td)} style={{ color: 'var(--gray-500)', fontSize: 11 }}>
                    {new Date(m.grantedAt).toLocaleDateString()}
                  </td>
                  <td className={css(s.td)} style={{ textAlign: 'right' }}>
                    {canManage && m.roleId !== 'owner' && (
                      <button
                        className={css(s.btnIcon)}
                        onClick={() => handleRemove(m)}
                        title="Remove"
                      >
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {canManage && invites && invites.length > 0 && (
          <>
            <div
              className={css(s.pageTitle)}
              style={{ fontSize: 14, marginTop: 28, marginBottom: 8 }}
            >
              Pending invites
            </div>
            <table className={css(s.table)}>
              <thead>
                <tr>
                  <th className={css(s.th)}>Invite</th>
                  <th className={css(s.th)}>Role</th>
                  <th className={css(s.th)}>Expires</th>
                  <th className={css(s.th)} />
                </tr>
              </thead>
              <tbody>
                {invites.map((i) => (
                  <tr key={i.id}>
                    <td className={css(s.td)}>
                      {i.email ||
                        `Shareable link · used ${i.useCount}${
                          i.maxUses != null ? ` of ${i.maxUses}` : ''
                        }`}
                    </td>
                    <td className={css(s.td)} style={{ textTransform: 'capitalize' }}>
                      {i.roleSlug === 'pm' ? 'PM' : i.roleSlug}
                    </td>
                    <td className={css(s.td)} style={{ color: 'var(--gray-500)', fontSize: 11 }}>
                      {i.expiresAt ? new Date(i.expiresAt).toLocaleDateString() : 'Never'}
                    </td>
                    <td className={css(s.td)} style={{ textAlign: 'right' }}>
                      <button
                        className={css(s.btnIcon)}
                        onClick={() => handleRevoke(i)}
                        title="Revoke"
                      >
                        Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {showInvite && (
          <InviteModal
            orgSlug={slug}
            onClose={() => setShowInvite(false)}
            onCreated={() => {
              reload();
              reloadInvites();
            }}
          />
        )}
      </div>
    </SettingsLayout>
  );
}
